/** @format */

import React, { useState, useEffect, useContext } from "react";
import { useHistory, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import SpinnerLoading from "../Common/SpinnerLoading";
import * as dateHelper from "../../Helpers/DateHelper";
import { posServices } from "../../Services";
import ListAdminButtons from "../Common/ListAdminButtons";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { AuthContext } from "../../Contexts/AuthContext";
import AsyncSelect from "react-select/async";
import iconcontentcalendar from "../../Images/Icons/iconcontentcalendar.png";
import { useForm, Controller } from "react-hook-form";
import { posVersionService } from "../../Services/posVersion.services";
import Highcharts from "highcharts";
import BasicChart from "highcharts-react-official";

import DatePicker, { registerLocale } from "react-datepicker";
import { fil } from "date-fns/locale";

const Reporting = () => {
  const history = useHistory();
  const { userId, roleId } = useContext(AuthContext);
  const { t: translator } = useTranslation();
  const {
    updateLocName,
    updateLocPos,
    updateLocTenant,
    updateLocStore,
    updateLocTab,
    updateLocKey,
    isMobile,
    locTenant,
  } = useContext(GlobalContext);

  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  });
  const { handleSubmit, errors, control, setValue } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [list, setList] = useState([]);
  const [update, setUpdate] = useState(new Date());

  const [filters, setFilters] = useState({
    version: "",
    validFrom: null,
    onDate: false,
  });
  const [versionObj, setVersionObj] = useState({
    id: 0,
    label: translator("Select-Version"),
    value: translator("Select-Version"),
  });

  // Statistics state
  const [statistics, setStatistics] = useState([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [showChart, setShowChart] = useState(true);
  const [statsDateFrom, setStatsDateFrom] = useState(null);
  const [statsDateTo, setStatsDateTo] = useState(null);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  React.useEffect(() => {
    updateLocName("version-reporting");
    updateLocTenant(0);
    updateLocStore(0);
    updateLocPos(0);
    updateLocTab(0);
    updateLocKey(0);
  }, []);

  const promiseOptions = (inputValue) => {
    return posVersionService
      .getAll()
      .then((res) => {
        var result = res.data.map((e) => ({
          id: e.id,
          label: e.name,
          value: e.name,
        }));
        result.unshift({
          id: 0,
          label: translator("Select-Version"),
          oib: "",
        });
        return result;
      })
      .catch((err) => err);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setPagination({ ...pagination, pageIndex: 0 });
    setUpdate(new Date());
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setIsLoading(true);
    setPagination({
      ...pagination,
      pageIndex: sizePerPage != pagination.pageSize ? 0 : page - 1,
      pageSize: sizePerPage,
    });
    setUpdate(new Date());
  };

  const RemoteAll = ({ data, page, sizePerPage, totalSize, onTableChange }) => (
    <div>
      <BootstrapTable
        remote
        keyField="posVersionId"
        data={data}
        columns={columns}
        noDataIndication={translator("No-Data")}
        rowClasses="boostrap-table-row"
        pagination={paginationFactory({
          page,
          sizePerPage,
          totalSize,
        })}
        onTableChange={onTableChange}
      />
    </div>
  );

  const columns = [
    {
      dataField: "version",
      text: translator("POSVersion-List-Name"),
    },
    {
      dataField: "total",
      text: translator("POSVersion-List-Count"),
    },
  ];

  const handleToSearch = (event) => {
    setIsLoading(true);
    posVersionService
      .reporting(filters.version, filters.validFrom, !filters.onDate)
      .then((res) => {
        console.log("res", res);
        setList(res.data);
        setPagination({
          ...pagination,
          totalCount: res.totalCount,
          pageIndex: 0,
        });
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err.response);
      });
  };

  const handleStatsSearch = () => {
    setIsStatsLoading(true);
    posVersionService
      .reportingUsage(
        statsDateFrom ? `${statsDateFrom.getFullYear()}-${String(statsDateFrom.getMonth() + 1).padStart(2, "0")}-${String(statsDateFrom.getDate()).padStart(2, "0")}` : null,
        statsDateTo ? `${statsDateTo.getFullYear()}-${String(statsDateTo.getMonth() + 1).padStart(2, "0")}-${String(statsDateTo.getDate()).padStart(2, "0")}` : null
      )
      .then((res) => {
        setStatistics(res.data.data || []);
        setIsStatsLoading(false);
      })
      .catch(() => setIsStatsLoading(false));
  };

  const chartOptions = {
    chart: { type: "pie", height: "280px" },
    title: { text: null },
    tooltip: {
      pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
      },
    },
    series: [
      {
        name: translator("POSVersion-List-Count"),
        data: Array.isArray(statistics)
          ? statistics.map((item) => ({ name: item.version, y: item.total }))
          : [],
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div className="p-2">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("PosVersion-List-pageTitle")} </span>
        </div>
        <div className="card-body">
          <div className="row">
            {/* LEFT COLUMN - existing functionality */}
            <div className="col-md-7">
              <fieldset className="scheduler-border">
                <legend className="scheduler-border">
                  {translator("POS-List-labelFilters")}
                </legend>
                <form className="row" onSubmit={handleSubmit(handleToSearch)}>
                  <div className="form-group col-lg-4">
                    <label htmlFor="versionddl">
                      {translator("Administration-PosVersion-Name")}
                    </label>
                    <AsyncSelect
                      name="posVersionId"
                      onChange={(event) => {
                        setVersionObj({
                          id: event.id,
                          label: event.label,
                          value: event.value,
                        });
                        setFilters({ ...filters, version: event.value });
                      }}
                      defaultOptions={true}
                      loadOptions={promiseOptions}
                      placeholder={translator("Select-Version")}
                      value={versionObj}
                    />
                  </div>
                  <div className="form-group col-lg-4 col-md-5 col-sm-6">
                    <label>{translator("InvIssReport-ValidOnDay")}</label>
                    <div className="input-group mb-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text" id="basic-addon1">
                          <img src={iconcontentcalendar} alt="calendar" />
                        </span>
                      </div>
                      <Controller
                        maxDate={new Date()}
                        as={DatePicker}
                        control={control}
                        autoComplete="off"
                        className="form-control"
                        dateFormat="dd.MM.yyyy"
                        showYearDropdown={true}
                        name="validFrom"
                        rules={{ required: false }}
                        locale="hr"
                        valueName="selected"
                        onChange={function ([selected]) {
                          const nextDay = new Date(selected);
                          nextDay.setDate(nextDay.getDate() + 1);
                          handleInputChange({
                            target: {
                              name: "validFrom",
                              value: nextDay
                                ? nextDay.toISOString().split("T")[0]
                                : null,
                            },
                          });
                          return selected;
                        }}
                      />
                    </div>
                    {errors.validFrom && errors.validFrom.type === "required" && (
                      <p className="text-danger">
                        {translator("ValidationMsg-RequiredField")}
                      </p>
                    )}
                  </div>
                  <div className="form-group col-lg-2 col-md-3 col-xs-12">
                    <label className="labelCont mt-5">
                      <input
                        type="checkbox"
                        name="onDate"
                        checked={filters.onDate}
                        onChange={(event) => {
                          setFilters({
                            ...filters,
                            onDate: event.target.checked,
                          });
                          setPagination({ ...pagination, pageIndex: 0 });
                        }}
                      />
                      <span className="checkmark"></span>
                    </label>
                    <span>{translator("ManagingVersion-OnDate")}</span>
                  </div>
                  <div className="form-group col-lg-2 col-md-4 col-sm-6">
                    <label htmlFor="searchBtn"> </label>
                    <div className="form-group">
                      <button
                        className="btn btn-primary pull-left mt-2"
                        type="submit"
                      >
                        <span className="icon icon-search"></span>{" "}
                        {translator("InvIssReport-Btn-Search")}
                      </button>
                    </div>
                  </div>
                </form>
              </fieldset>

              {isLoading && (
                <div className="dashboard-card-spinner">
                  <SpinnerLoading />
                </div>
              )}

              {!isLoading && (
                <RemoteAll
                  data={list}
                  page={pagination.pageIndex + 1}
                  sizePerPage={pagination.pageSize}
                  noDataIndication={translator("No-Data")}
                  totalSize={pagination.totalCount}
                  onTableChange={handleTableChange}
                />
              )}
            </div>

            {/* RIGHT COLUMN - statistics */}
            <div className="col-md-5">
              <fieldset className="scheduler-border">
                <legend className="scheduler-border">
                  {translator("Support-Statistics")}
                </legend>

                <div className="d-flex" style={{ gap: 8 }}>
                  <div className="form-group flex-fill">
                    <label>{translator("DateFrom") || "Od"}</label>
                    <div className="input-group mb-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text">
                          <img src={iconcontentcalendar} alt="calendar" />
                        </span>
                      </div>
                      <DatePicker
                        selected={statsDateFrom}
                        onChange={(date) => setStatsDateFrom(date)}
                        dateFormat="dd.MM.yyyy"
                        className="form-control"
                        autoComplete="off"
                        maxDate={statsDateTo || new Date()}
                        placeholderText="dd.MM.yyyy"
                      />
                    </div>
                  </div>

                  <div className="form-group flex-fill">
                    <label>{translator("DateTo") || "Do"}</label>
                    <div className="input-group mb-2">
                      <div className="input-group-prepend">
                        <span className="input-group-text">
                          <img src={iconcontentcalendar} alt="calendar" />
                        </span>
                      </div>
                      <DatePicker
                        selected={statsDateTo}
                        onChange={(date) => setStatsDateTo(date)}
                        dateFormat="dd.MM.yyyy"
                        className="form-control"
                        autoComplete="off"
                        minDate={statsDateFrom}
                        maxDate={new Date()}
                        placeholderText="dd.MM.yyyy"
                      />
                    </div>
                  </div>
                </div>

                <button
                  className="btn btn-primary btn-sm mb-3"
                  onClick={handleStatsSearch}
                  disabled={isStatsLoading}
                >
                  <span className="icon icon-search"></span>{" "}
                  {translator("InvIssReport-Btn-Search")}
                </button>
              </fieldset>

              {isStatsLoading ? (
                <div className="dashboard-card-spinner">
                  <SpinnerLoading />
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>{translator("Support-Statistics")}</strong>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setShowChart((prev) => !prev)}
                    >
                      {showChart
                        ? translator("Support-ShowList")
                        : translator("Support-ShowChart")}
                    </button>
                  </div>

                  {!showChart ? (
                    <ul className="list-group">
                      <li
                        className="list-group-item d-flex justify-content-between align-items-center"
                        style={{ background: "#f8f9fa", fontWeight: 600, fontSize: 12, color: "#000" }}
                      >
                        <span>{translator("POSVersion-List-Name")}</span>
                        <span style={{ minWidth: 60, textAlign: "center" }}>
                          {translator("POSVersion-List-Count")}
                        </span>
                      </li>
                      {Array.isArray(statistics) && statistics.map((item, i) => (
                        <li
                          key={i}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{item.version}</span>
                          <span
                            className="badge badge-pill"
                            style={{ minWidth: 60, backgroundColor: "#e20074", color: "#fff" }}
                          >
                            {item.total}
                          </span>
                        </li>
                      ))}
                      {(!statistics || statistics.length === 0) && (
                        <li className="list-group-item text-muted">
                          {translator("No-Data")}
                        </li>
                      )}
                    </ul>
                  ) : (
                    <BasicChart highcharts={Highcharts} options={chartOptions} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Reporting;
